import { apiFetch } from "@/lib/api";

export const CORRECTIVE_ACTION_STATUSES = [
  "draft",
  "open",
  "containment",
  "analysis",
  "action",
  "verification",
  "closed",
  "cancelled",
] as const;

export type CorrectiveActionStatus = (typeof CORRECTIVE_ACTION_STATUSES)[number];

export const CORRECTIVE_ACTION_SOURCES = [
  "customer_complaint",
  "rma",
  "internal_audit",
  "qc_incident",
  "supplier",
  "incoming_quality",
  "management_review",
  "process_deviation",
  "other",
] as const;

export type CorrectiveActionSource = (typeof CORRECTIVE_ACTION_SOURCES)[number];

export interface CorrectiveActionReport {
  id: number;
  carNumber: string;
  title: string;
  status: CorrectiveActionStatus;
  sourceType: CorrectiveActionSource;
  openedAt: string;
  targetCloseDate: string | null;
  closedAt: string | null;
  reportedBy: string;
  reportedByDisplay: string | null;
  ownerUserId: string | null;
  ownerDisplay: string | null;
  problemDescription: string;
  immediateContainment: string | null;
  rootCauseAnalysis: string | null;
  correctiveActionPlan: string | null;
  implementationNotes: string | null;
  effectivenessVerification: string | null;
  continuedReviewOn: string | null;
  preventiveAction: string | null;
  clientFsAccountId: number | null;
  clientName: string | null;
  salesOrderId: number | null;
  salesOrderReference: string | null;
  qualityIncidentId: number | null;
  qualityEventId: number | null;
  qualityEventSummary: string | null;
  qualityCaseId: number | null;
  qualityCaseTitle: string | null;
  serialFciId: number | null;
  deviceId: number | null;
  deviceLabelSerial: string | null;
  closedBy: string | null;
  closedByDisplay: string | null;
  verifiedBy: string | null;
  verifiedByDisplay: string | null;
  verifiedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CorrectiveActionActivity {
  id: number;
  correctiveActionReportId: number;
  activityAt: string;
  actorEmail: string;
  actorDisplay: string | null;
  actionType: string;
  fieldName: string | null;
  oldValue: string | null;
  newValue: string | null;
  note: string | null;
}

export interface CorrectiveActionListResponse {
  rows: CorrectiveActionReport[];
  totalCount: number;
  limit: number;
  offset: number;
}

export interface CorrectiveActionDetailResponse {
  report: CorrectiveActionReport;
  activities: CorrectiveActionActivity[];
}

export const CORRECTIVE_ACTIONS_LIST_KEY = ["quality", "corrective-actions"] as const;

export function correctiveActionLabel(id: number): string {
  return `CAR ${id}`;
}

export function correctiveActionStatusLabel(status: CorrectiveActionStatus): string {
  switch (status) {
    case "draft":
      return "Draft";
    case "open":
      return "Open";
    case "containment":
      return "Containment";
    case "analysis":
      return "Root cause analysis";
    case "action":
      return "Corrective action";
    case "verification":
      return "Verification";
    case "closed":
      return "Closed";
    case "cancelled":
      return "Cancelled";
    default:
      return status;
  }
}

export function correctiveActionSourceLabel(source: CorrectiveActionSource): string {
  switch (source) {
    case "customer_complaint":
      return "Customer complaint";
    case "rma":
      return "RMA";
    case "internal_audit":
      return "Internal audit";
    case "qc_incident":
      return "QC incident";
    case "supplier":
      return "Supplier";
    case "incoming_quality":
      return "Incoming quality";
    case "management_review":
      return "Management review";
    case "process_deviation":
      return "Process deviation";
    case "other":
      return "Other";
    default:
      return source;
  }
}

/** Event sources. RMA is a return record, not an event. Labels are alphabetical, with Other last. */
export function qualityEventSourceOptions(): CorrectiveActionSource[] {
  return CORRECTIVE_ACTION_SOURCES.filter((source) => source !== "rma").sort((left, right) => {
    if (left === "other") return 1;
    if (right === "other") return -1;
    return correctiveActionSourceLabel(left).localeCompare(correctiveActionSourceLabel(right), "en", {
      sensitivity: "base",
    });
  });
}

export interface CorrectiveActionListFilters {
  status?: CorrectiveActionStatus;
  sourceType?: CorrectiveActionSource;
  search?: string;
  limit?: number;
  offset?: number;
}

function buildListQuery(filters: CorrectiveActionListFilters): string {
  const params = new URLSearchParams();
  if (filters.status) params.set("status", filters.status);
  if (filters.sourceType) params.set("sourceType", filters.sourceType);
  if (filters.search?.trim()) params.set("search", filters.search.trim());
  if (filters.limit != null) params.set("limit", String(filters.limit));
  if (filters.offset != null) params.set("offset", String(filters.offset));
  const query = params.toString();
  return query.length > 0 ? `?${query}` : "";
}

export async function fetchCorrectiveActions(
  filters: CorrectiveActionListFilters = {},
): Promise<CorrectiveActionListResponse> {
  return apiFetch<CorrectiveActionListResponse>(`/api/quality/corrective-actions${buildListQuery(filters)}`);
}

export async function fetchCorrectiveActionDetail(id: number): Promise<CorrectiveActionDetailResponse> {
  return apiFetch<CorrectiveActionDetailResponse>(`/api/quality/corrective-actions/${id}`);
}

export interface CreateCorrectiveActionInput {
  title: string;
  sourceType?: CorrectiveActionSource;
  problemDescription?: string;
  immediateContainment?: string;
  ownerUserId?: string | null;
  targetCloseDate?: string;
  clientFsAccountId?: number | null;
  salesOrderId?: number | null;
  qualityIncidentId?: number | null;
  serialFciId?: number | null;
  deviceId?: number | null;
  qualityCaseId?: number | null;
}

export async function createCorrectiveAction(input: CreateCorrectiveActionInput): Promise<CorrectiveActionReport> {
  return apiFetch<CorrectiveActionReport>("/api/quality/corrective-actions", {
    method: "POST",
    body: (input),
  });
}

export type UpdateCorrectiveActionInput = Partial<{
  title: string;
  status: CorrectiveActionStatus;
  sourceType: CorrectiveActionSource;
  targetCloseDate: string | null;
  ownerUserId: string | null;
  problemDescription: string;
  immediateContainment: string | null;
  rootCauseAnalysis: string | null;
  correctiveActionPlan: string | null;
  implementationNotes: string | null;
  effectivenessVerification: string | null;
  continuedReviewOn: string | null;
  preventiveAction: string | null;
  clientFsAccountId: number | null;
  salesOrderId: number | null;
  qualityIncidentId: number | null;
  qualityCaseId: number | null;
  serialFciId: number | null;
  deviceId: number | null;
}>;

export async function updateCorrectiveAction(
  id: number,
  input: UpdateCorrectiveActionInput,
): Promise<CorrectiveActionReport> {
  return apiFetch<CorrectiveActionReport>(`/api/quality/corrective-actions/${id}`, {
    method: "PATCH",
    body: (input),
  });
}

export async function addCorrectiveActionComment(id: number, note: string): Promise<CorrectiveActionActivity> {
  return apiFetch<CorrectiveActionActivity>(`/api/quality/corrective-actions/${id}/activities`, {
    method: "POST",
    body: ({ note }),
  });
}

export async function fetchCorrectiveActionPdf(
  id: number,
): Promise<{ filename: string; pdfBase64: string; correctAsOf: string }> {
  return apiFetch<{ filename: string; pdfBase64: string; correctAsOf: string }>(
    `/api/quality/corrective-actions/${String(id)}/pdf`,
  );
}
